import React, {useEffect, useState} from 'react';

import {useGate, useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {useLocation} from 'react-router-dom';

import {ROUTES} from '@/app/routing';
import {
    $invocieBillingBinaryPdf,
    $subscriptionStatus,
    setInvocieBillingBinaryPdf,
    SubscriptionsGate,
} from '@/entities/subscriptions/model';
import {DownloadInvoice} from '@/pages/Subscriptions/DownloadInvoice';
import {Icon} from '@shared/ui/Icon';
import {MainLayoutProps} from '@shared/ui/Layout/default/types';

import * as S from './styled';

export const MainLayout: React.FC<MainLayoutProps> = ({
    children,
    headerSlot,
    logoSlot,
    asideItems = [],
    profilePath,
    asideHidden = false,
}) => {
    const invocieBillingBinaryPdf = useStore($invocieBillingBinaryPdf);
    const subscriptionStatus = useStore($subscriptionStatus);

    const [isOpen, setIsOpen] = useState(false);
    const toggle = () => setIsOpen(!isOpen);
    const {t} = useTranslation();
    const location = useLocation();
    useGate(SubscriptionsGate, {isSubscriptionsPage: location.pathname === ROUTES.SUBSCRIPTIONS});

    const [showDownloadInvoice, setShowDownloadInvoice] = useState(false);
    const handleClose = () => {
        setInvocieBillingBinaryPdf(null);
    };

    useEffect(() => {
        if (invocieBillingBinaryPdf) {
            setShowDownloadInvoice(true);
        } else {
            setShowDownloadInvoice(false);
        }
    }, [subscriptionStatus, invocieBillingBinaryPdf]);

    const Logo = () => {
        if (!logoSlot) return '';
        if (isOpen) return logoSlot.full ?? logoSlot.small;
        return logoSlot.small ?? '';
    };

    return (
        <S.LayoutWrapper>
            <DownloadInvoice show={showDownloadInvoice} onClose={handleClose} />
            {!asideHidden && (
                <S.Aside isOpen={isOpen}>
                    <S.LogoWrapper>{Logo?.() || ''}</S.LogoWrapper>

                    <S.AsideGroup>
                        <S.AsideSection>
                            {asideItems.map((item, index) =>
                                item.external ? (
                                    <S.AsideExternalItem
                                        key={index}
                                        href={item.to}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={item.label}
                                        title={item.label}
                                    >
                                        <Icon name={item.icon} />
                                        <S.ItemText isOpen={isOpen}>{item.label}</S.ItemText>
                                    </S.AsideExternalItem>
                                ) : (
                                    <S.AsideItem key={index} to={item.to} activeClassName="active">
                                        <Icon name={item.icon} />
                                        <S.ItemText isOpen={isOpen}>{item.label}</S.ItemText>
                                    </S.AsideItem>
                                ),
                            )}
                        </S.AsideSection>

                        <S.AsideSection>
                            {profilePath && (
                                <S.AsideItem to={profilePath} activeClassName="active">
                                    <Icon name="options" />
                                    <S.ItemText isOpen={isOpen}>
                                        {t('menu.control.settings')}
                                    </S.ItemText>
                                </S.AsideItem>
                            )}

                            <S.AsideToggle onClick={toggle}>
                                <Icon name="menu" />
                                <S.ItemText isOpen={isOpen}>{t('menu.control.close')}</S.ItemText>
                            </S.AsideToggle>
                        </S.AsideSection>
                    </S.AsideGroup>
                </S.Aside>
            )}

            <S.PageWrapper asideHidden={asideHidden}>
                <S.Header>{headerSlot}</S.Header>
                <S.Main>{children}</S.Main>
            </S.PageWrapper>
        </S.LayoutWrapper>
    );
};
